import React, { FormEvent, useState } from 'react';
import { Head, router } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';

interface InviteSignupProps {
  token: string;
  inviteName?: string;
  errors?: Record<string, string>;
  formData?: {
    name?: string;
    email?: string;
  };
}

const InviteSignup: React.FC<InviteSignupProps> = ({
  token,
  inviteName,
  errors = {},
  formData = {},
}) => {
  const [name, setName] = useState(formData.name || '');
  const [email, setEmail] = useState(formData.email || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Email validation regex pattern
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  const validateEmail = (emailToValidate: string): boolean => {
    return emailRegex.test(emailToValidate);
  };

  const validatePassword = (pass: string, passConf: string): boolean => {
    if (pass.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return false;
    }
    if (pass !== passConf) {
      setPasswordError('Passwords do not match');
      return false;
    }
    setPasswordError('');
    return true;
  };

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    
    // Client-side email validation
    if (!validateEmail(email)) {
      return;
    }
    
    // Client-side password validation
    if (!validatePassword(password, passwordConfirmation)) {
      return;
    }
    
    setProcessing(true);
    
    // Use router.post for full control over the data
    router.post(`/public/invites/${token}`, {
      user: {
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
        agreed: agreed ? '1' : '0'
      }
    });
  }

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <Head title="Create Your Account">
        <meta property="og:title" content="Create Your Account" />
        <meta property="og:description" content="Accept your invitation to join" />
        <meta property="og:image" content="https://hd.supavp.com/og-image.svg" />
        <meta property="og:url" content={`https://hd.supavp.com/public/invites/${token}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Create Your Account" />
        <meta name="twitter:description" content="Accept your invitation to join" />
        <meta name="twitter:image" content="https://hd.supavp.com/og-image.svg" />
      </Head>

      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Create Your Account
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {inviteName ? `You've been invited by ${inviteName}` : 'Accept your invitation to join'}
          </p>
        </div>

        {/* Form Card */}
        <Card className="p-5 sm:p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Global Errors */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
                <p className="font-medium">Please correct the following errors:</p>
                <ul className="list-disc list-inside mt-2">
                  {Object.entries(errors).map(([key, value]) => (
                    <li key={key}>
                      {typeof value === 'string' ? value : Array.isArray(value) ? value[0] : value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Name Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                required
                className={errors.name ? 'border-destructive' : ''}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                className={errors.password ? 'border-destructive' : ''}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="password_confirmation">Confirm Password</Label>
              <Input
                id="password_confirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                placeholder="Confirm your password"
                required
                className={(passwordError || errors.password_confirmation) ? 'border-destructive' : ''}
              />
              {(passwordError || errors.password_confirmation) && (
                <p className="text-sm text-destructive">{passwordError || errors.password_confirmation}</p>
              )}
            </div>

            {/* Consent Checkbox */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="consent"
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked === true)}
                className="mt-1"
              />
              <div className="flex flex-col">
                <label
                  htmlFor="consent"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  I agree to the{' '}
                  <a href="/terms" className="text-primary hover:underline" target="_blank">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" className="text-primary hover:underline" target="_blank">
                    Privacy Policy
                  </a>
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  I have read and agree to the terms governing my use of the service
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <Button
                type="submit"
                className="w-full"
                disabled={processing || !agreed}
              >
                {processing ? 'Creating Account...' : 'Create Account'}
              </Button>
            </div>
          </form>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-xs sm:text-sm text-muted-foreground">
          <p>This is a one-time invite link. Your account will be created securely.</p>
        </div>
      </div>
    </div>
  );
};

export default InviteSignup;
